import { Button, Card, CardContent } from '@tark/shared/components';
import { ArrowRight, CalendarClock, Loader2 } from 'lucide-react';
import type { CoachNextSessionPanelProps } from './coachStudioTypes';
import { formatCompactDate, formatSessionWindow } from './coachStudioUtils';

export default function CoachNextSessionPanel({
  occurrence,
  rosterSummary,
  attendanceSummary,
  makeupSummary,
  openSpotCount,
  slotFillerCount,
  isLoading,
  onOpenWork,
}: CoachNextSessionPanelProps) {
  const capacity = rosterSummary?.capacity ?? occurrence?.capacity ?? null;
  const bookedCount = rosterSummary?.bookedCount ?? null;
  const attendanceRequired = attendanceSummary?.requiredCount ?? bookedCount ?? 0;
  const attendanceDone =
    (attendanceSummary?.presentCount ?? 0) +
    (attendanceSummary?.lateCount ?? 0) +
    (attendanceSummary?.absentCount ?? 0) +
    (attendanceSummary?.excusedCount ?? 0);
  const attendancePct = attendanceRequired > 0 ? Math.min(100, Math.round((attendanceDone / attendanceRequired) * 100)) : 0;
  const rosterValue = bookedCount != null
    ? capacity != null
      ? `${bookedCount}/${capacity}`
      : String(bookedCount)
    : '-';

  if (!occurrence) {
    return (
      <Card variant="default" padding="none" className="overflow-hidden border-primary/15">
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <CalendarClock className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Next session</p>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight text-text">No session queued</h2>
                <p className="mt-1.5 max-w-xl text-sm font-medium text-text-muted">
                  Create the first session or add availability from Schedule.
                </p>
              </div>
            </div>

            <Button onClick={onOpenWork} rightIcon={<ArrowRight className="h-4 w-4" />}>
              Open Schedule
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="none" className="overflow-hidden border-primary/15">
      <div className="border-b border-border bg-white px-5 py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                Next up
              </span>
              {occurrence ? (
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">
                  {formatCompactDate(occurrence.startsAt)} · {formatSessionWindow(occurrence.startsAt, occurrence.endsAt)}
                </span>
              ) : null}
            </div>
            <h2 className="mt-3 truncate text-2xl font-semibold tracking-tight text-text">
              {occurrence?.title ?? 'No session queued'}
            </h2>
            {occurrence ? (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium text-text-muted">
                {occurrence.programName ? <span>{occurrence.programName}</span> : null}
                {occurrence.locationLabel ? <span>{occurrence.locationLabel}</span> : null}
                {occurrence.coachName ? <span>{occurrence.coachName}</span> : null}
              </div>
            ) : null}
          </div>

          <Button onClick={onOpenWork} rightIcon={<ArrowRight className="h-4 w-4" />} className="shrink-0">
            Open Roster
          </Button>
        </div>
      </div>

      <CardContent className="space-y-4 p-5">
        {isLoading && occurrence ? (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Loading roster state
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <CoachSessionSignal label="Roster" value={rosterValue} />
          <CoachSessionSignal label="Check-in" value={`${attendanceDone} of ${attendanceRequired}`} />
          {openSpotCount != null && openSpotCount > 0 ? <CoachSessionSignal label="Open spots" value={openSpotCount} tone="attention" /> : null}
          {(makeupSummary?.openCount ?? 0) > 0 ? <CoachSessionSignal label="Makeups" value={makeupSummary?.openCount ?? 0} /> : null}
          {slotFillerCount > 0 ? <CoachSessionSignal label="Fillers ready" value={slotFillerCount} tone="attention" /> : null}
        </div>

        {attendanceRequired > 0 ? (
          <div className="rounded-lg border border-border bg-surface-elevated/20 px-4 py-3">
            <div className="flex items-center justify-between gap-4 text-sm">
              <span className="font-semibold text-text">Attendance</span>
              <span className="font-semibold text-primary">{attendancePct}% marked</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${attendancePct}%` }} />
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CoachSessionSignal({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  tone?: 'default' | 'attention';
}) {
  return (
    <span
      className={
        tone === 'attention'
          ? 'inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary'
          : 'inline-flex items-center gap-2 rounded-full border border-border bg-surface-elevated/30 px-3 py-1.5 text-sm font-semibold text-text'
      }
    >
      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</span>
      <span>{value}</span>
    </span>
  );
}
